/**
 * Teaching guide for instructors (admin only) — printable.
 * Renders a guide for the given protocolId. Data lives in dedicated
 * `teachingGuideDataLot*.js` and `teachingGuideDataLegacy.js` files.
 */
import { useEffect } from 'react';
import { Printer, ChevronLeft, Heart, AlertTriangle, ShieldAlert, CheckCircle, MessageSquare, BookOpen, Clock, Users, Smartphone } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { buildTeachingGuides as buildLot1 } from './teachingGuideDataLot1';
import { buildTeachingGuidesLot2 as buildLot2 } from './teachingGuideDataLot2';
import { buildTeachingGuidesLot3 as buildLot3 } from './teachingGuideDataLot3';
import { buildTeachingGuidesLot4 as buildLot4 } from './teachingGuideDataLot4';
import { buildTeachingGuidesLegacy } from './teachingGuideDataLegacy';
import { SELF_EVAL_QUIZZES } from './selfEvalQuizzes';

const ICONS = { Heart, AlertTriangle, ShieldAlert, Clock, Users };

const GUIDES = {
  ...buildLot1(ICONS),
  ...buildLot2(ICONS),
  ...buildLot3(ICONS),
  ...buildLot4(ICONS),
  ...buildTeachingGuidesLegacy(ICONS),
};

const GuideSection = ({ icon: Icon, title, accent = '#002768', children }) => (
  <section className="mb-8 break-inside-avoid">
    <div className="flex items-center gap-2 mb-3 pb-2 border-b-2" style={{ borderColor: accent }}>
      <Icon className="w-5 h-5" style={{ color: accent }} />
      <h2 className="text-xl font-bold" style={{ color: accent }}>{title}</h2>
    </div>
    <div className="text-sm text-gray-800 leading-relaxed">{children}</div>
  </section>
);

const Pill = ({ children, color = '#002768' }) => (
  <span
    className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold mr-1.5 mb-1.5"
    style={{ backgroundColor: `${color}15`, color }}
  >
    {children}
  </span>
);

const TeachingGuide = ({ protocolId, onExit }) => {
  const guide = GUIDES[protocolId];

  useEffect(() => {
    // Inject print-friendly CSS once
    const styleId = 'teaching-guide-print-style';
    if (document.getElementById(styleId)) return;
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      @media print {
        body * { visibility: hidden; }
        #teaching-guide-print, #teaching-guide-print * { visibility: visible; }
        #teaching-guide-print { position: absolute; left: 0; top: 0; width: 100%; padding: 1cm; background: white; }
        .no-print { display: none !important; }
        section { page-break-inside: avoid; }
        h1, h2, h3 { page-break-after: avoid; }
      }
    `;
    document.head.appendChild(style);
  }, []);

  if (!guide) {
    return (
      <div className="max-w-3xl mx-auto py-12 px-4 text-center" data-testid="teaching-guide-not-ready">
        <BookOpen className="w-16 h-16 mx-auto text-gray-300 mb-4" />
        <h2 className="text-xl font-bold text-gray-700 mb-2">Guide pédagogique à venir</h2>
        <p className="text-gray-500 mb-6">
          Le guide pour ce déterminant n’est pas encore rédigé.
          Demandez-le à votre équipe pour qu’il soit ajouté à la prochaine itération.
        </p>
        <button
          onClick={onExit}
          className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold"
          data-testid="teaching-guide-back-button"
        >
          ← Retour
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-6 px-4" data-testid="teaching-guide-page">
      {/* Sticky toolbar — sits below the fixed navigation bar (h-16) */}
      <div className="sticky top-20 z-30 flex justify-between items-center mb-6 no-print bg-white/95 backdrop-blur-sm rounded-xl shadow border border-gray-200 p-2">
        <button
          onClick={onExit}
          className="flex items-center gap-1 px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-sm font-semibold text-gray-700"
          data-testid="teaching-guide-exit-button"
        >
          <ChevronLeft className="w-4 h-4" /> Retour aux protocoles
        </button>
        <button
          onClick={() => window.print()}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-semibold shadow hover:opacity-90"
          style={{ backgroundColor: '#002768' }}
          data-testid="teaching-guide-print-button"
        >
          <Printer className="w-4 h-4" /> Imprimer le guide
        </button>
      </div>

      {/* Floating print FAB — bottom-LEFT to avoid Emergent badge on bottom-right */}
      <button
        type="button"
        onClick={() => window.print()}
        className="no-print fixed bottom-6 left-6 z-40 flex items-center gap-2 px-5 py-3 rounded-full text-white text-sm font-bold shadow-lg hover:scale-105 transition-transform"
        style={{ backgroundColor: '#002768' }}
        data-testid="teaching-guide-print-fab"
        title="Imprimer ce guide pédagogique"
      >
        <Printer className="w-5 h-5" /> Imprimer
      </button>

      {/* Printable content */}
      <article id="teaching-guide-print" className="bg-white rounded-2xl shadow-sm p-8 border border-gray-100">
        <header className="mb-8 pb-6 border-b-2 border-gray-200">
          <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold mb-2">
            Simulateur 911 — Guide pédagogique pour instructeurs
          </p>
          <h1 className="text-3xl font-bold text-gray-900 mb-1">{guide.title}</h1>
          <p className="text-gray-600">{guide.subtitle}</p>
        </header>

        <GuideSection icon={BookOpen} title="Contexte et importance">
          <p className="mb-3">{guide.intro}</p>
          <div className="mt-4 space-y-2">
            {guide.stats.map((s, i) => {
              const I = s.icon;
              return (
                <div key={i} className="flex items-start gap-2 p-3 rounded-lg bg-blue-50">
                  <I className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-700" />
                  <span className="text-sm">{s.text}</span>
                </div>
              );
            })}
          </div>
        </GuideSection>

        <GuideSection icon={CheckCircle} title="Objectifs d’apprentissage" accent="#16a34a">
          <ol className="list-decimal pl-5 space-y-1">
            {guide.objectives.map((o, i) => <li key={i}>{o}</li>)}
          </ol>
        </GuideSection>

        <GuideSection icon={MessageSquare} title={guide.questioning ? guide.questioning.title : "Le questionnement MPDS — rappel rapide"} accent="#7c3aed">
          {guide.questioning ? (
            <>
              <p className="mb-3">{guide.questioning.body}</p>
              <h3 className="font-semibold text-gray-800 mb-2">Principes fondamentaux :</h3>
              <ul className="list-disc pl-5 space-y-1">
                {guide.questioning.principles.map((p, i) => <li key={i}>{p}</li>)}
              </ul>
            </>
          ) : (
            <p className="text-sm">
              <strong>Rappel :</strong> Le MPDS est un questionnement structuré en entonnoir (sécurité → clinique → priorité).
              Une question à la fois, réponses fermées avant ouvertes, reformulation systématique.
              <em> Voir le guide « Douleur Thoracique » pour la description complète des 5 principes fondamentaux.</em>
            </p>
          )}
        </GuideSection>

        <GuideSection icon={Clock} title="Les phases du protocole, expliquées" accent="#2563eb">
          {guide.compactCommon && (
            <div className="mb-4 p-3 rounded-lg bg-blue-50 border-l-4 border-blue-400 text-xs">
              <strong>Phase 1 — Contrôle initial de l’appel :</strong> identique pour tous les déterminants
              (adresse exacte + numéro de rappel + identité appelant + âge + sexe).
              <em> Détails complets dans le guide « Douleur Thoracique ».</em>
            </div>
          )}
          <p className="text-xs text-gray-500 italic mb-4">
            Pour chaque phase : pourquoi elle existe, comment la poser, et les pièges classiques.
          </p>
          {guide.phases.map(p => (
            <div key={p.n} className="mb-5 p-4 rounded-lg bg-gray-50 border-l-4 border-blue-500 break-inside-avoid">
              <h3 className="font-bold text-gray-900 mb-1">
                <span className="inline-block w-7 h-7 rounded-full bg-blue-600 text-white text-xs font-bold leading-7 text-center mr-2">
                  {p.n}
                </span>
                {p.title}
              </h3>
              <p className="text-xs text-gray-600 italic mb-2"><strong>Pourquoi :</strong> {p.why}</p>
              <p className="text-xs font-semibold text-gray-700 mt-2">Comment poser la question :</p>
              <ul className="list-disc pl-5 text-xs space-y-0.5">
                {p.how.map((h, i) => <li key={i}>{h}</li>)}
              </ul>
              <p className="text-xs font-semibold text-red-700 mt-2">⚠ Pièges fréquents :</p>
              <ul className="list-disc pl-5 text-xs space-y-0.5 text-red-700">
                {p.pitfalls.map((h, i) => <li key={i}>{h}</li>)}
              </ul>
            </div>
          ))}
        </GuideSection>

        <GuideSection icon={Users} title={guide.citizen_behavior.title} accent="#c2410c">
          <h3 className="font-semibold mb-2">🎤 Ton et posture vocale</h3>
          <ul className="list-disc pl-5 space-y-1 mb-4">
            {guide.citizen_behavior.tone.map((t, i) => <li key={i}>{t}</li>)}
          </ul>

          <h3 className="font-semibold mb-2 text-green-700">✅ Phrases utiles</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
            {guide.citizen_behavior.phrases_utiles.map((p, i) => (
              <div key={i} className="p-2 bg-green-50 rounded text-xs italic border-l-2 border-green-400">
                {p}
              </div>
            ))}
          </div>

          <h3 className="font-semibold mb-2 text-red-700">❌ Phrases à éviter</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
            {guide.citizen_behavior.phrases_a_eviter.map((p, i) => (
              <div key={i} className="p-2 bg-red-50 rounded text-xs italic border-l-2 border-red-400">
                {p}
              </div>
            ))}
          </div>

          <h3 className="font-semibold mb-2">🧘 Gestion de la panique</h3>
          <ul className="list-disc pl-5 space-y-1">
            {guide.citizen_behavior.panique_gestion.map((p, i) => <li key={i}>{p}</li>)}
          </ul>
        </GuideSection>

        <GuideSection icon={ShieldAlert} title="Drapeaux rouges à reconnaître" accent="#dc2626">
          <p className="text-xs text-gray-500 italic mb-3">
            Plus le nombre de drapeaux rouges est élevé, plus la priorité doit être haute.
          </p>
          <div>
            {guide.red_flags.map((rf, i) => <Pill key={i} color="#dc2626">🚩 {rf}</Pill>)}
          </div>
        </GuideSection>

        <GuideSection icon={AlertTriangle} title="Consignes pré-arrivée à donner au citoyen" accent="#f59e0b">
          <ol className="list-decimal pl-5 space-y-1">
            {guide.pre_arrival.map((p, i) => <li key={i}>{p}</li>)}
          </ol>
        </GuideSection>

        <GuideSection icon={AlertTriangle} title="Erreurs courantes des étudiants" accent="#dc2626">
          <ul className="list-disc pl-5 space-y-1">
            {guide.common_errors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </GuideSection>

        <GuideSection icon={MessageSquare} title="Questions de discussion pour la classe" accent="#7c3aed">
          <p className="text-xs text-gray-500 italic mb-3">
            Idéal pour amorcer un débat ou une mise en situation en sous-groupes.
          </p>
          <ol className="list-decimal pl-5 space-y-2">
            {guide.discussion_questions.map((q, i) => <li key={i}>{q}</li>)}
          </ol>
        </GuideSection>

        <GuideSection icon={BookOpen} title="Références et lectures suggérées">
          <ul className="list-disc pl-5 space-y-1 text-xs">
            {guide.references.map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        </GuideSection>

        {/* Bottom print button — also hidden on print */}
        <div className="no-print mt-10 flex justify-center">
          <button
            onClick={() => window.print()}
            className="flex items-center gap-2 px-6 py-3 rounded-lg text-white text-sm font-bold shadow hover:opacity-90"
            style={{ backgroundColor: '#002768' }}
            data-testid="teaching-guide-print-button-bottom"
          >
            <Printer className="w-5 h-5" /> Imprimer le guide complet
          </button>
        </div>

        {SELF_EVAL_QUIZZES[protocolId] && (
          <section
            className="mt-10 p-6 rounded-2xl border-2 break-inside-avoid flex flex-col sm:flex-row items-center gap-6"
            style={{ borderColor: '#002768', backgroundColor: '#f8fafc' }}
            data-testid="teaching-guide-qr-section"
          >
            <div className="flex-shrink-0 p-3 bg-white rounded-xl border border-gray-200 shadow-sm">
              <QRCodeSVG
                value={`${window.location.origin}/m/guide/${protocolId}`}
                size={144}
                level="M"
                bgColor="#ffffff"
                fgColor="#002768"
                includeMargin={false}
                data-testid="teaching-guide-qr-code"
              />
            </div>
            <div className="flex-1 text-center sm:text-left">
              <div className="flex items-center gap-2 justify-center sm:justify-start mb-2">
                <Smartphone className="w-5 h-5" style={{ color: '#002768' }} />
                <h3 className="text-base font-bold" style={{ color: '#002768' }}>
                  Version mobile + auto-évaluation
                </h3>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed mb-2">
                Scannez ce code QR avec votre téléphone pour consulter ce guide sur mobile et
                tester vos connaissances avec un quiz de {SELF_EVAL_QUIZZES[protocolId].questions.length} questions.
              </p>
              <p className="text-xs text-gray-500 break-all no-print">
                {window.location.origin}/m/guide/{protocolId}
              </p>
            </div>
          </section>
        )}

        <footer className="mt-12 pt-6 border-t border-gray-200 text-xs text-gray-400 text-center">
          Simulateur 911 — Document pédagogique • Date d’impression : {new Date().toLocaleDateString('fr-CA')}
        </footer>
      </article>
    </div>
  );
};

export default TeachingGuide;
